import { useEffect, useState, type FormEvent } from "react";
import { AlertCircle, Eye, EyeOff, LoaderCircle, LockKeyhole, RefreshCw } from "lucide-react";
import { App } from "./App.tsx";
import { api, ApiError, AUTH_REQUIRED_EVENT } from "./api.ts";
import { ThemeToggleBtn } from "./theme.tsx";

interface AuthSession {
  authenticated: boolean;
  user: { username: string; role: "ADMIN" } | null;
  auth_mode: "local" | "oidc" | "development";
}

export function AuthGate() {
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loadError, setLoadError] = useState("");

  const loadSession = async () => {
    setLoadError("");
    try {
      setSession(await api.get<AuthSession>("/api/auth/session"));
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : "无法连接服务器");
    }
  };

  useEffect(() => {
    void loadSession();
    const handleAuthRequired = () => void loadSession();
    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
  }, []);

  if (loadError) {
    return (
      <AuthStatus icon={<AlertCircle />} title="连接失败" detail={loadError}>
        <button type="button" className="auth-secondary-button" onClick={() => void loadSession()}><RefreshCw />重试</button>
      </AuthStatus>
    );
  }
  if (!session) return <AuthStatus icon={<LoaderCircle className="spin" />} title="正在验证会话" />;
  if (!session.authenticated) {
    return session.auth_mode === "local"
      ? <LoginPage onAuthenticated={setSession} />
      : (
        <AuthStatus icon={<LockKeyhole />} title="访问认证未完成">
          <button type="button" className="auth-primary-button" onClick={() => window.location.reload()}><RefreshCw />重新验证</button>
        </AuthStatus>
      );
  }

  return <App currentUser={session.user!} onLogout={async () => {
    await api.post("/api/auth/logout");
    setSession({ ...session, authenticated: false, user: null });
  }} />;
}

function LoginPage({ onAuthenticated }: { onAuthenticated: (session: AuthSession) => void }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      onAuthenticated(await api.post<AuthSession>("/api/auth/login", { username, password }));
    } catch (cause) {
      setError(cause instanceof ApiError && cause.code === "LOGIN_RATE_LIMITED" ? "登录尝试过多，请稍后再试" : "用户名或密码错误");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-top-actions">
        <ThemeToggleBtn />
      </div>
      <section className="auth-panel" aria-labelledby="login-title">
        <div className="auth-brand">
          <span className="brand-mark">A</span>
          <div><strong>Ads Operations</strong></div>
        </div>
        <div className="auth-heading">
          <span className="auth-heading-icon"><LockKeyhole /></span>
          <div><h1 id="login-title">管理员登录</h1><p>Amazon 广告运营控制台</p></div>
        </div>
        <form onSubmit={submit} className="auth-form">
          <div className="auth-field">
            <label htmlFor="admin-username">用户名</label>
            <input id="admin-username" value={username} onChange={(event) => setUsername(event.target.value)} autoComplete="username" autoFocus required />
          </div>
          <div className="auth-field">
            <label htmlFor="admin-password">密码</label>
            <div className="auth-password-field">
              <input id="admin-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} autoComplete="current-password" required />
              <button type="button" onClick={() => setShowPassword((current) => !current)} title={showPassword ? "隐藏密码" : "显示密码"} aria-label={showPassword ? "隐藏密码" : "显示密码"}>
                {showPassword ? <EyeOff /> : <Eye />}
              </button>
            </div>
          </div>
          {error && <div className="auth-error" role="alert"><AlertCircle />{error}</div>}
          <button type="submit" className="auth-primary-button" disabled={submitting}>
            {submitting ? <LoaderCircle className="spin" /> : <LockKeyhole />}
            {submitting ? "正在登录" : "登录"}
          </button>
        </form>
      </section>
    </main>
  );
}

function AuthStatus({ icon, title, detail, children }: { icon: React.ReactNode; title: string; detail?: string; children?: React.ReactNode }) {
  return (
    <main className="auth-page">
      <div className="auth-top-actions">
        <ThemeToggleBtn />
      </div>
      <section className="auth-panel auth-status">
        <span className="auth-status-icon">{icon}</span>
        <h1>{title}</h1>
        {detail && <p>{detail}</p>}
        {children}
      </section>
    </main>
  );
}
